
import { useState, useEffect } from 'react';
import PricingService from '@/services/pricingService';

// Define the cost constants
export type Platform = 'iOS' | 'Android' | 'Web' | 'CrossPlatform';
export type AppType = 'MVP' | 'Full' | 'Enterprise';
export type Feature = 'auth' | 'logo' | 'customUI' | 'chat' | 'payments' | 'gps' | 'backend' | 'adminPanel' | 'apiIntegration' | 'push' | 'offline' | 'cloudSync';
export type UserVolume = 'small' | 'medium' | 'large' | 'enterprise';
export type Maintenance = '6' | '12' | '24';

export interface EstimationOptions {
  platforms: Platform[];
  appType?: AppType;
  features: Feature[];
  userVolume?: UserVolume;
  maintenance?: Maintenance;
}

export interface CostBreakdown {
  platformCost: number;
  appTypeCost: number;
  featuresCost: number;
  userVolumeCost: number;
  maintenanceCost: number;
  total: number;
}

export const useCostCalculator = (initialOptions?: Partial<EstimationOptions>) => {
  const [options, setOptions] = useState<EstimationOptions>({
    platforms: [],
    features: [],
    ...initialOptions,
  });

  const [costs, setCosts] = useState<CostBreakdown>({
    platformCost: 0,
    appTypeCost: 0,
    featuresCost: 0,
    userVolumeCost: 0,
    maintenanceCost: 0,
    total: 0,
  });
  
  const [COSTS, setCOSTS] = useState({
    platforms: { iOS: 0, Android: 0, Web: 0, CrossPlatform: 0 },
    appType: { MVP: 0, Full: 0, Enterprise: 0 },
    features: {
      auth: 0, logo: 0, customUI: 0, chat: 0, payments: 0, gps: 0,
      backend: 0, adminPanel: 0, apiIntegration: 0, push: 0, offline: 0, cloudSync: 0
    },
    userVolume: { small: 0, medium: 0, large: 0, enterprise: 0 },
    maintenance: { '6': 0, '12': 0, '24': 0 }
  });
  
  const [loading, setLoading] = useState(true);

  // Fetch pricing data when component mounts
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        const pricingService = PricingService.getInstance();
        const pricingData = await pricingService.getPricingData();
        setCOSTS(pricingData);
      } catch (error) {
        console.error('Failed to fetch pricing data:', error);
        // Could implement fallback or error state here
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  // Calculate costs whenever options or COSTS change
  useEffect(() => {
    if (loading) return;
    
    const platformCost = options.platforms.reduce(
      (acc, platform) => acc + COSTS.platforms[platform],
      0
    );
    
    const appTypeCost = options.appType ? COSTS.appType[options.appType] : 0;
    
    const featuresCost = options.features.reduce(
      (acc, feature) => acc + COSTS.features[feature],
      0
    );
    
    const userVolumeCost = options.userVolume ? COSTS.userVolume[options.userVolume] : 0;
    
    const maintenanceCost = options.maintenance ? COSTS.maintenance[options.maintenance] : 0;
    
    const total = platformCost + appTypeCost + featuresCost + userVolumeCost + maintenanceCost;
    
    setCosts({
      platformCost,
      appTypeCost,
      featuresCost,
      userVolumeCost,
      maintenanceCost,
      total
    });
  }, [options, COSTS, loading]);

  // Function to update options
  const updateOptions = (newOptions: Partial<EstimationOptions>) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      ...newOptions
    }));
  };

  // Functions to manipulate specific option types
  const togglePlatform = (platform: Platform) => {
    setOptions(prevOptions => {
      if (prevOptions.platforms.includes(platform)) {
        return {
          ...prevOptions,
          platforms: prevOptions.platforms.filter(p => p !== platform)
        };
      } else {
        return {
          ...prevOptions,
          platforms: [...prevOptions.platforms, platform]
        };
      }
    });
  };

  const setAppType = (appType: AppType) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      appType
    }));
  };

  const toggleFeature = (feature: Feature) => {
    setOptions(prevOptions => {
      if (prevOptions.features.includes(feature)) {
        return {
          ...prevOptions,
          features: prevOptions.features.filter(f => f !== feature)
        };
      } else {
        return {
          ...prevOptions,
          features: [...prevOptions.features, feature]
        };
      }
    });
  };

  const setUserVolume = (userVolume: UserVolume) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      userVolume
    }));
  };

  const setMaintenance = (maintenance: Maintenance | undefined) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      maintenance
    }));
  };

  return {
    options,
    costs,
    updateOptions,
    togglePlatform,
    setAppType,
    toggleFeature,
    setUserVolume,
    setMaintenance,
    COSTS,
    loading
  };
};

export default useCostCalculator;
